import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ayreneService, getMinutesUntilExpiration, getTokenExpiration } from '../../services/ayrene.service';

export const useTokenRefresh = () => {
    const [showWarning, setShowWarning] = useState(false);
    const [minutesRemaining, setMinutesRemaining] = useState(0);
    const navigate = useNavigate();
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const warningShownRef = useRef(false);

    const checkTokenExpiration = useCallback(async () => {
        const token = localStorage.getItem('ayreneAuthToken');
        const refreshToken = localStorage.getItem('ayreneRefreshToken');

        if (!token || !refreshToken) {
            return;
        }

        const minutes = getMinutesUntilExpiration(token);

        if (minutes <= 0) {
            console.log('🚪 Token expired, logging out...');
            handleLogout();
            return;
        }

        if (minutes <= 5 && !warningShownRef.current) {
            console.log(`⚠️ Token expires in ${minutes} minutes, showing warning...`);
            setMinutesRemaining(minutes);
            setShowWarning(true);
            warningShownRef.current = true;
        }

        if (minutes <= 2 && minutes > 0) {
            console.log('🔄 Token expires in 2 minutes or less, refreshing automatically...');
            await handleRefreshToken();
        }
    }, []);

    const handleRefreshToken = async () => {
        try {
            const result = await ayreneService.refreshToken();

            if (result.success) {
                console.log('✅ Token refreshed successfully');
                setShowWarning(false);
                warningShownRef.current = false;
            } else {
                console.error('❌ Token refresh failed:', result.message);
                handleLogout();
            }
        } catch (error) {
            console.error('❌ Token refresh error:', error);
            handleLogout();
        }
    };

    const handleStayLoggedIn = async () => {
        setShowWarning(false);
        await handleRefreshToken();
    };

    const handleLogout = useCallback(() => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('ayreneAuthToken');
        localStorage.removeItem('ayreneRefreshToken');
        localStorage.removeItem('ayreneTokenExpiration');
        localStorage.removeItem('ayreneLoginStatus');

        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
        }

        setShowWarning(false);
        warningShownRef.current = false;

        navigate('/login');
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem('ayreneAuthToken');

        if (!token) {
            return;
        }

        checkTokenExpiration();

        checkIntervalRef.current = setInterval(() => {
            checkTokenExpiration();
        }, 60000);

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    }, [checkTokenExpiration]);

    return {
        showWarning,
        minutesRemaining,
        handleStayLoggedIn,
        handleLogout,
    };
};
